import React from "react";
import { useTranslation } from "react-i18next";
import { ColumnColor, colorVars, DivisionResult, divisionWinner, formatAvg } from "./contentTeamShared";
import HostAvatar from "./HostAvatar";
import styles from "./ContentTeam.module.css";

export interface HostSeason {
  steamId: string;
  name: string;
  color: ColumnColor;
  /** Place on the season's prediction leaderboard, when it loaded and the host is on it. */
  rank: number | null;
  avg: number | null;
  exact: number | null;
}

interface Props {
  season: number;
  hosts: HostSeason[];
  results: DivisionResult[];
  predictors: number;
  /** The page header, drawn as the panel's top strip. */
  children?: React.ReactNode;
}

/** The top of the page: the header, then who leads the season over every division. */
const SeasonHeadToHead: React.FC<Props> = ({ season, hosts, results, predictors, children }) => {
  const { t } = useTranslation("pages");
  const [a, b] = hosts;

  const wins = [0, 0];
  let ties = 0;
  let scored = 0;
  for (const result of results) {
    const win = divisionWinner(result.scores);
    if (win === null) continue;
    scored++;
    if (win === -1) ties++;
    else wins[win]++;
  }

  // Rounded like the leaderboard, so "leads by 0.00" can't happen.
  const ra = a.avg === null ? null : Math.round(a.avg * 100);
  const rb = b.avg === null ? null : Math.round(b.avg * 100);
  const lead = ra === null || rb === null || ra === rb ? null : ra < rb ? 0 : 1;
  let headline = t("contentTeam.waiting");
  let line1 = "";
  let line2 = "";
  if (scored > 0) {
    if (lead === null) {
      headline = t("contentTeam.even");
      line1 = t("contentTeam.sameAvg");
    } else {
      headline = t("contentTeam.leads", { name: hosts[lead].name });
      line1 = t("contentTeam.leadBy", { diff: (Math.abs((ra ?? 0) - (rb ?? 0)) / 100).toFixed(2) });
    }
    const leader = lead ?? 0;
    const other = 1 - leader;
    if (lead !== null && wins[other] > wins[leader]) {
      line2 = t("contentTeam.mostDivisions", { name: hosts[other].name, won: wins[other], lost: wins[leader] });
    } else if (lead !== null && wins[leader] > wins[other]) {
      line2 = t("contentTeam.alsoMostDivisions", { name: hosts[leader].name, won: wins[leader], lost: wins[other] });
    } else {
      line2 = t("contentTeam.divisionsEven", { a: wins[0], b: wins[1] });
    }
    if (ties > 0) line2 += t("contentTeam.ties", { count: ties });
  }

  const side = (host: HostSeason, index: number) => (
    <div className={`${styles.side} ${index === 1 ? styles.sideRight : ""}`} style={colorVars(host.color)}>
      <HostAvatar steamId={host.steamId} name={host.name} color={host.color} size="lg" />
      <div className={styles.sideText}>
        <span className={styles.sideName}>
          <span className={styles.sideNameText}>{host.name}</span>
          {host.rank !== null && (
            <span className={styles.muted}>{t("contentTeam.lbRank", { rank: host.rank, total: predictors })}</span>
          )}
        </span>
        <span className={styles.sideStats}>
          <span className={styles.stat}>
            <span className={styles.statValue}>{scored > 0 ? formatAvg(host.avg) : "–"}</span>
            <span className={styles.statLabel}>{t("contentTeam.avg")}</span>
          </span>
          <span className={styles.stat}>
            <span className={styles.statValue}>{scored > 0 && host.exact !== null ? host.exact : "–"}</span>
            <span className={styles.statLabel}>{t("contentTeam.exact")}</span>
          </span>
          <span className={styles.stat}>
            <span className={`${styles.statValue} ${styles.statValueAccent}`}>{scored > 0 ? wins[index] : "–"}</span>
            <span className={styles.statLabel}>{t("contentTeam.divisionsWon")}</span>
          </span>
        </span>
      </div>
    </div>
  );

  return (
    <section className={`${styles.panel} ${styles.heroPanel}`} aria-label={t("contentTeam.h2hTitle", { season })}>
      {children}
      <div className={styles.h2hBody}>
        {side(a, 0)}
        <div className={styles.center} style={lead !== null ? colorVars(hosts[lead].color) : undefined}>
          <span className={styles.centerLabel}>{t("contentTeam.wholeSeason")}</span>
          <span className={styles.headline}>{headline}</span>
          {line1 && <span className={styles.muted}>{line1}</span>}
          {line2 && <span className={styles.muted}>{line2}</span>}
          {scored > 0 && <span className={styles.centerRule}>{t("contentTeam.centerRule")}</span>}
        </div>
        {side(b, 1)}
      </div>
    </section>
  );
};

export default SeasonHeadToHead;
